/**
 * FF Delivery Availability Manager
 * Centralizes all delivery availability logic to avoid 409 conflicts
 * and ensure cart attributes + line item properties are always set.
 */
window.ffDeliveryAvailability = (function () {
  let isProcessing = false;
  let pendingRefresh = false;
  let isReady = false;

  /**
   * Returns whether the availability data has been set and checkout is safe
   * @returns {boolean}
   */
  function getIsReady() {
    return isReady && !isProcessing;
  }

  /**
   * Disables all checkout buttons until availability is set
   */
  function disableCheckout() {
    isReady = false;
    document.body.classList.remove("ff-checkout-ready");
  }

  /**
   * Enables all checkout buttons after availability is set
   */
  function enableCheckout() {
    isReady = true;
    document.body.classList.add("ff-checkout-ready");
  }

  /**
   * Fetches availability info from a product page
   * @param {string} productUrl
   * @returns {Promise<{text: string, colorClass: string, promiseMessage: string, containerEl: Element, promiseEl: Element, msgElement: Element} | null>}
   */
  async function fetchProductAvailability(productUrl) {
    try {
      const response = await fetch(productUrl);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");

      const containerEl = doc.querySelector(".ff-estimated-delivery-container");
      const promiseEl = containerEl?.querySelector(".ff-estimated-delivery-promise");
      const msgElement = promiseEl?.querySelector(".ff-estimated-delivery-availability");

      if (containerEl && promiseEl && msgElement) {
        return {
          text: containerEl.dataset.ffAvailability || msgElement.textContent?.trim() || "",
          colorClass: promiseEl.className.replace("ff-estimated-delivery-promise", "").trim() || "",
          promiseMessage: containerEl.dataset.ffPromiseMessage || "",
          containerEl,
          promiseEl,
          msgElement,
        };
      }
    } catch (e) {
      console.error("Error fetching product availability:", e);
    }
    return null;
  }

  /**
   * Extracts availability data from a container element
   * @param {Element} containerEl
   * @returns {{colorClass: string, availabilityMsg: string, availabilityDateMsg: string}}
   */
  function extractAvailabilityFromContainer(containerEl) {
    const promiseEl = containerEl.querySelector(".ff-estimated-delivery-promise");
    return {
      colorClass: promiseEl?.className.replace("ff-estimated-delivery-promise", "").trim() || "",
      availabilityMsg: containerEl.dataset.ffAvailability || "",
      availabilityDateMsg: containerEl.dataset.ffPromiseMessage || "",
    };
  }

  /**
   * Gets the cart-level availability from the cart page footer
   * If not on cart page, fetches the cart page to get the data
   * @returns {Promise<{colorClass: string, availabilityMsg: string, availabilityDateMsg: string} | null>}
   */
  async function getCartAvailabilityAttribute() {
    const containerEl = document.querySelector(
      "#main-cart-footer .ff-estimated-delivery-container"
    );

    if (containerEl) {
      return extractAvailabilityFromContainer(containerEl);
    }

    // Not on cart page - fetch cart page to get availability
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");

      const fetchedContainerEl = doc.querySelector(
        "#main-cart-footer .ff-estimated-delivery-container"
      );

      if (fetchedContainerEl) {
        return extractAvailabilityFromContainer(fetchedContainerEl);
      }
    } catch (e) {
      console.error("Error fetching cart page for availability:", e);
    }

    return null;
  }

  /**
   * Updates cart attributes and all line item properties in a single call
   * @param {Object} cartAttribute - The _availabilityObj for cart attributes
   * @param {Object} lineProperties - Map of line item key -> {text, colorClass}
   */
  async function updateCart(cartAttribute, lineProperties) {
    try {
      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cartData = await cartResponse.json();

      const updates = {};
      let hasPropertyChanges = false;

      for (const item of cartData.items) {
        const lineId = item.id.toString();
        const newAvailability = lineProperties[lineId];

        if (newAvailability) {
          const currentText = item.properties?._ff_delivery_availability;
          const currentClass = item.properties?._ff_delivery_availability_class;

          if (currentText !== newAvailability.text || currentClass !== newAvailability.colorClass) {
            hasPropertyChanges = true;
          }

          updates[item.key] = {
            ...item.properties,
            _ff_delivery_availability: newAvailability.text,
            _ff_delivery_availability_class: newAvailability.colorClass,
          };
        }
      }

      const payload = {
        attributes: {
          _availabilityObj: JSON.stringify(cartAttribute),
        },
      };

      await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (hasPropertyChanges && Object.keys(updates).length > 0) {
        for (const [key, properties] of Object.entries(updates)) {
          await fetch(`${window.Shopify.routes.root}cart/change.js`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: key,
              properties: properties,
            }),
          });

          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

    } catch (e) {
      console.error("Error updating cart availability:", e);
    }
  }

  /**
   * Updates the visual availability display for a line item
   * @param {Element} targetContainerEl - The container element in the DOM to update
   * @param {Element} sourcePromiseEl - The promise element from fetched HTML
   * @param {Element} sourceMsgElement - The message element from fetched HTML
   * @param {boolean} isDrawer - Whether this is for the cart drawer
   */
  function updateLineItemUI(targetContainerEl, sourcePromiseEl, sourceMsgElement, isDrawer) {
    const clonedPromise = sourcePromiseEl.cloneNode(true);
    const clonedMsg = sourceMsgElement.cloneNode(true);

    if (isDrawer) {
      clonedPromise.className = clonedPromise.className.replaceAll(
        "ff-estimated-delivery",
        "ff-cart-drawer_estimated-delivery"
      );
      clonedMsg.className = clonedMsg.className.replaceAll(
        "ff-estimated-delivery",
        "ff-cart-drawer_estimated-delivery"
      );
    }

    clonedPromise.textContent = "";
    clonedPromise.appendChild(clonedMsg);

    targetContainerEl.innerHTML = "";
    targetContainerEl.appendChild(clonedPromise);
  }

  /**
   * Main refresh function - loads all availability data and updates cart
   */
  async function refresh() {
    if (isProcessing) {
      pendingRefresh = true;
      return;
    }

    isProcessing = true;
    disableCheckout();

    try {
      const lineProperties = {};

      const cartPageLines = document.querySelectorAll(
        "cart-items [data-ff-line-item]"
      );
      const drawerLines = document.querySelectorAll(
        "cart-drawer ff-cart-item[data-ff-line-id]"
      );

      const allLines = [
        ...Array.from(cartPageLines).map((el) => ({
          el,
          id: el.dataset.ffLineItem,
          url: el.dataset.ffProductUrl,
          isDrawer: false,
        })),
        ...Array.from(drawerLines).map((el) => ({
          el,
          id: el.dataset.ffLineId,
          url: el.dataset.ffProductUrl,
          isDrawer: true,
        })),
      ];

      const uniqueUrls = [...new Set(allLines.map((l) => l.url).filter(Boolean))];
      const availabilityCache = {};

      for (const url of uniqueUrls) {
        const availability = await fetchProductAvailability(url);
        if (availability) {
          availabilityCache[url] = availability;
        }
      }

      for (const line of allLines) {
        if (line.url && availabilityCache[line.url]) {
          const availability = availabilityCache[line.url];

          if (availability.text && line.id) {
            lineProperties[line.id] = {
              text: availability.text,
              colorClass: availability.colorClass,
            };
          }

          const containerEl = line.el.querySelector(
            "[data-ff-delivery-availability]"
          );
          if (containerEl && availability.promiseEl && availability.msgElement) {
            updateLineItemUI(
              containerEl,
              availability.promiseEl,
              availability.msgElement,
              line.isDrawer
            );
          }
        }
      }

      const cartAttribute = await getCartAvailabilityAttribute();

      if (cartAttribute || Object.keys(lineProperties).length > 0) {
        await updateCart(cartAttribute || {}, lineProperties);
      }

      const drawerDateEl = document.querySelector("[data-ff-availability-date]");
      if (drawerDateEl && cartAttribute) {
        drawerDateEl.textContent = cartAttribute.availabilityDateMsg || "";
      }

      const loaderEl = document.querySelector("[data-ff-availability-date-loader]");
      if (loaderEl) {
        delete loaderEl.dataset.ffLoading;
      }
    } catch (e) {
      console.error("Error in availability refresh:", e);
    } finally {
      isProcessing = false;

      if (pendingRefresh) {
        pendingRefresh = false;
        setTimeout(refresh, 200);
      } else {
        enableCheckout();
      }
    }
  }

  /**
   * Checks if an element is a checkout-related button or inside a checkout container
   * @param {HTMLElement} el
   * @returns {boolean}
   */
  function isCheckoutElement(el) {
    return !!(el.closest(".ff-checkout-btn") || el.closest(".ff-checkout-buttons"));
  }

  /**
   * Intercepts checkout form submissions to prevent checkout while processing
   */
  function setupCheckoutInterceptor() {
    document.addEventListener("submit", (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;

      const hasCheckoutButton = form.querySelector(".ff-checkout-btn");
      if (!hasCheckoutButton) return;

      if (!getIsReady()) {
        e.preventDefault();
        e.stopPropagation();
        console.warn("Checkout blocked: delivery availability still processing");
      }
    }, true);

    document.addEventListener("click", (e) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;

      if (!isCheckoutElement(target)) return;

      if (!getIsReady()) {
        e.preventDefault();
        e.stopPropagation();
        console.warn("Checkout blocked: delivery availability still processing");
      }
    }, true);
  }

  /**
   * Initializes the availability manager
   */
  function init() {
    disableCheckout();
    setupCheckoutInterceptor();
    refresh();

    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source !== "ff-delivery-availability") {
          disableCheckout();
          setTimeout(refresh, 300);
        }
      });
    }
  }

  return {
    refresh,
    init,
    getIsReady,
    disableCheckout,
  };
})();

document.addEventListener("DOMContentLoaded", () => {
  window.ffDeliveryAvailability.init();
});
